import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Globe, BookOpen, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { peopleData } from '../data/peopleData';

const FellowsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_vegr-highland/artifacts/4v6ip53w_PNG.png" 
                alt="Highland Institute Vintage Logo" 
                className="h-60 w-auto filter brightness-0 invert"
              />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              The Highland Fellowship
            </h1>
            <p className="text-2xl text-green-100 leading-relaxed">
              A network of world-class scholars engaged in research and teaching across Highland Asia
            </p>
          </div>
        </div>
      </section>

      {/* About the Fellowship */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">About the Fellowship</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                The <strong>Highland Fellowship</strong> seeks to attract world-class scholars actively engaged in 
                both research and teaching in conjunction with partner institutions and granting agencies. Often based 
                in staff positions in other universities around the world, they engage in both short and long-term 
                research projects in South and Southeast Asia, and often work closely with postgraduate and early 
                career researchers at the Institute in collecting, processing and publishing data and findings.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Fellows may spend several weeks to several months engaged in fieldwork, and organise as well as 
                present their ongoing work in the regular calendar of seminars, meetings and activities organised 
                by the Highland Institute. Time permitting, they engage in intensive or term-length modular teaching 
                at primarily the postgraduate and early career research levels, and occasionally conduct research 
                training workshops in local partner colleges and universities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Fellows Do */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What Fellows Do</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Globe className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Fieldwork</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Conduct weeks to months of intensive research in Highland Asia communities
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Mentorship</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Work closely with postgraduate and early career researchers
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <BookOpen className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Teaching</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Intensive modular teaching and research training workshops
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Seminars</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Organize and present research in Institute seminars and activities
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Current Fellows */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">Current Highland Fellows</h2>
            <p className="text-center text-gray-600 max-w-2xl mx-auto">
              Our distinguished network of scholars from around the world contributing to research and education in Highland Asia
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {peopleData.fellows.map((fellow, index) => (
              <Link
                key={index}
                to={`/people/${fellow.id}`}
                className="block"
              >
                <Card className="hover:shadow-lg transition-shadow cursor-pointer hover:border-green-500">
                  <CardContent className="p-4">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-24 h-24 rounded-full overflow-hidden mb-3 bg-gray-200">
                        <img 
                          src={fellow.image} 
                          alt={fellow.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h4 className="text-sm font-bold text-gray-900 hover:text-green-600 transition-colors">{fellow.name}</h4>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Become a Fellow */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Becoming a Fellow</h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 leading-relaxed mb-6">
                Highland Fellows are nominated in recognition of their sustained contributions to the Institute's 
                mission and vision. The Fellowship is open to scholars, researchers, and practitioners who have 
                demonstrated excellence in their field and commitment to collaborative, community-engaged research 
                in Highland Asia.
              </p>
              <div className="border-l-4 border-green-600 pl-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Selection Criteria</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Active engagement in research relevant to Highland Asia</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Commitment to mentorship and knowledge sharing</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Collaborative approach to research and teaching</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Alignment with Institute's values and mission</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Join Our Community</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Learn more about fellowship opportunities and how to engage with the Highland Institute
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/programmes">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Study With Us
              </Button>
            </Link>
            <a href="mailto:info@highlandinstitute.org">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FellowsPage;
